import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useI18n, useLocalized } from '../lib/i18n';
import { filterServices } from '../lib/service-search';
import type { Service } from '../lib/types';
import { Label } from './ui';

/** What the picker hands back: a catalogue service, or a name typed by hand. */
export type ServiceChoice =
  | { kind: 'service'; serviceId: string }
  | { kind: 'custom'; customName: string };

interface Props {
  services: readonly Service[];
  value: ServiceChoice | null;
  onChange: (choice: ServiceChoice) => void;
  label?: string;
  autoFocus?: boolean;
}

/**
 * Choosing a service by typing.
 *
 * A drop-down works while the catalogue fits on one screen. Past that, the
 * person at the counter is scrolling a list in front of a waiting customer, so
 * this searches instead — across all three languages, in any word order.
 *
 * Whatever is typed and does not match anything is still useful: it becomes a
 * one-off line with that exact name, which is how a shop records the job it
 * does once and never again.
 */
export function ServicePicker({ services, value, onChange, label, autoFocus }: Props) {
  const { t } = useI18n();
  const localized = useLocalized();
  const listId = useId();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const active = useMemo(
    () => services.filter((service) => service.isActive),
    [services],
  );
  const found = useMemo(() => filterServices(active, query), [active, query]);

  const typed = query.trim();
  // The one-off entry is always reachable: named after what was typed when
  // something was, and offered plainly when the field is still empty.
  const options: Array<{ choice: ServiceChoice; title: string; note: string | null }> = [
    ...found.map((service) => ({
      choice: { kind: 'service' as const, serviceId: service.id },
      title: localized(service.name),
      note: localized(service.categoryName),
    })),
    {
      choice: { kind: 'custom' as const, customName: typed },
      title: typed ? t('picker.oneOffNamed', { name: typed }) : t('neworder.oneOff'),
      note: null,
    },
  ];

  const chosen = value?.kind === 'service'
    ? active.find((service) => service.id === value.serviceId)
    : undefined;
  const shown = value === null ? null
    : value.kind === 'custom' ? (value.customName || t('neworder.oneOff'))
    : chosen ? localized(chosen.name) : null;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const pick = (choice: ServiceChoice) => {
    onChange(choice);
    setOpen(false);
    setQuery('');
    setCursor(0);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') { setOpen(false); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setCursor((prev) => (prev + step + options.length) % options.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[cursor] ?? options[options.length - 1];
      if (option) pick(option.choice);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-1.5" ref={boxRef}>
      {label && <Label>{label}</Label>}

      <div className="relative">
        <button
          type="button"
          autoFocus={autoFocus}
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex w-full min-w-0 items-center gap-2 rounded-xl border border-line bg-raised px-3.5 py-2.5 text-left text-ink transition-colors duration-150 hover:border-ink-faint focus:border-brand focus:outline-none"
        >
          <span className={`min-w-0 flex-1 truncate ${shown ? '' : 'text-ink-faint'}`}>
            {shown ?? t('picker.choose')}
          </span>
          <svg aria-hidden viewBox="0 0 12 8" className="h-2 w-3 shrink-0 text-ink-faint">
            <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        {open && (
          <div className="absolute inset-x-0 top-full z-30 mt-1.5 overflow-hidden rounded-xl border border-line bg-surface shadow-xl shadow-black/40">
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => { setQuery(event.target.value); setCursor(0); }}
              onKeyDown={onKeyDown}
              placeholder={t('picker.search')}
              aria-controls={listId}
              className="w-full border-b border-line bg-raised px-3.5 py-2.5 text-ink placeholder:text-ink-faint focus:outline-none"
            />
            <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto py-1">
              {options.map((option, index) => {
                const isCustom = option.choice.kind === 'custom';
                return (
                  <li key={option.choice.kind === 'custom' ? 'custom' : option.choice.serviceId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === cursor}
                      onMouseEnter={() => setCursor(index)}
                      onClick={() => pick(option.choice)}
                      className={`flex w-full items-baseline gap-2 px-3.5 py-2 text-left text-sm transition-colors ${
                        index === cursor ? 'bg-brand-wash' : ''
                      } ${isCustom ? 'border-t border-line text-brand' : index === cursor ? 'text-ink' : 'text-ink-dim'}`}
                    >
                      <span className="min-w-0 flex-1 truncate">{option.title}</span>
                      {option.note && (
                        <span className="shrink-0 text-[0.7rem] text-ink-faint">{option.note}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
